// Reports Catalogue — every entry is a LIVE report served by the generic
// report engine (/api/reports-engine). No roadmap slots, no dead tiles:
// click any card and it runs on your books immediately via the ReportShell.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AppShell } from '@/components/AppShell';
import { KbdChip } from '@/components/primitives/KbdChip';
import { cleanErr } from '@/lib/errMsg';
import { cn } from '@/lib/cn';

interface CatalogueRow {
  code: string;
  title: string;
  description?: string;
  category: string;
  family: string;
  dim: string;
  dimTitle: string;
  dimKind?: 'time' | 'ageing' | 'entity' | 'categorical';
  featuredMeasure: string;
}

// A tiny glyph per report class so the eye can tell trend / ageing / ranking /
// share reports apart at a glance in the grid.
const KIND_GLYPH: Record<string, string> = {
  time: '📈', ageing: '⏳', entity: '📊', categorical: '◔',
};

const CATEGORY_ORDER = [
  'Accounting',
  'Outstanding & ageing',
  'Inventory',
  'GST & statutory',
  'Banking',
  'Sales analysis',
  'Purchase analysis',
  'Exceptions & audit',
];

export function ReportsCataloguePage() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<string>('All');

  const catQ = useQuery<{ count: number; rows: CatalogueRow[] }>({
    queryKey: ['report-engine-catalogue'],
    queryFn: async () => (await api.get('/reports-engine/catalogue')).data,
    staleTime: 5 * 60 * 1000,
  });

  const rows = catQ.data?.rows ?? [];
  const categories = useMemo(() => {
    const present = new Set(rows.map((r) => r.category));
    return ['All', ...CATEGORY_ORDER.filter((c) => present.has(c)), ...[...present].filter((c) => !CATEGORY_ORDER.includes(c))];
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      (category === 'All' || r.category === category) &&
      (!needle || r.title.toLowerCase().includes(needle) || r.code.includes(needle)),
    );
  }, [rows, q, category]);

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogueRow[]>();
    for (const r of filtered) {
      if (!map.has(r.category)) map.set(r.category, []);
      map.get(r.category)!.push(r);
    }
    return [...map.entries()];
  }, [filtered]);

  return (
    <AppShell>
      <main className="mx-auto max-w-[1280px] px-4 py-6 sm:px-6">
        <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-display-md font-semibold text-obsidian">Reports Catalogue</h1>
            <p className="mt-1 text-body-md text-slate">
              {catQ.data ? `${catQ.data.count} reports — every one runs on your live books. ` : 'Every report runs on your live books. '}
              Each opens with a chart tuned to what it shows; time reports switch between Day / Week / Month in one click. Click any number to see the vouchers behind it.
            </p>
          </div>
          <a href="/reports" className="text-caption text-cool hover:text-cobalt">Curated hub →</a>
        </header>

        {/* Search + category filter */}
        <section className="mb-5 flex flex-wrap items-center gap-2">
          <div className="relative">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search reports… (try 'party', 'GST', 'ageing', 'trend')"
              className="h-10 w-72 rounded-md border border-mist bg-haze px-3 text-body-sm text-obsidian outline-none placeholder:text-cool focus:border-cobalt/60 focus:shadow-focus"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {categories.map((c) => (
              <button key={c} type="button" onClick={() => setCategory(c)}
                className={cn('rounded-md border px-2.5 py-1.5 text-caption transition',
                  category === c ? 'border-cobalt bg-cobalt/[0.05] text-cobalt' : 'border-mist text-slate hover:border-cobalt hover:text-cobalt')}>
                {c}
              </button>
            ))}
          </div>
        </section>

        {catQ.isLoading && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 12 }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-md bg-frost" />)}
          </div>
        )}
        {catQ.isError && (
          <div className="rounded-md border border-crimson/40 bg-crimson/[0.05] px-4 py-3 text-body-sm text-crimson">
            {cleanErr(catQ.error, "Couldn't load the catalogue.")}
          </div>
        )}
        {catQ.data && filtered.length === 0 && (
          <div className="rounded-md border border-mist bg-haze/70 px-4 py-8 text-center">
            <p className="text-body-md text-obsidian">No reports match "{q}".</p>
            <p className="mt-1 text-body-sm text-slate">Try a broader word — party, item, GST, month, ageing, cash…</p>
          </div>
        )}

        {grouped.map(([cat, items]) => (
          <section key={cat} className="mb-8">
            <h2 className="mb-3 text-micro font-medium uppercase tracking-[0.12em] text-cool">
              <span className="mr-2 inline-block h-[1px] w-4 bg-cobalt align-middle" />
              {cat} · {items.length}
            </h2>
            <div className="orz-stagger grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {items.map((r) => (
                <a key={r.code} href={`/r/${r.code}`}
                  className="group orz-lift flex flex-col rounded-md border border-mist bg-snow px-3 py-2.5 transition hover:border-cobalt hover:shadow-card"
                  style={{ backgroundImage: 'var(--wash-card)' }}
                  title={r.description ?? r.title}>
                  <div className="flex items-start gap-2">
                    {r.dimKind && <span aria-hidden className="text-body-sm leading-none">{KIND_GLYPH[r.dimKind]}</span>}
                    <div className="text-body-sm font-medium text-obsidian group-hover:text-cobalt">{r.title}</div>
                  </div>
                  {r.description && <p className="mt-1 line-clamp-2 text-caption text-slate">{r.description}</p>}
                  <div className="mt-1 font-mono text-nano text-cool">{r.code}</div>
                </a>
              ))}
            </div>
          </section>
        ))}

        <footer className="mt-6 border-t border-mist pt-3 text-caption text-cool print:hidden">
          Every report: period filters · sort any column · column chooser · totals · CSV · print · drill-down to vouchers.
          <span className="ml-2"><KbdChip>Ctrl K</KbdChip> jumps anywhere.</span>
        </footer>
      </main>
    </AppShell>
  );
}
